import React from 'react';
import { User, Shield, ChevronDown, LogOut, Settings, Users, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { announceToScreenReader } from '../../lib/accessibility';

export const ProfileDropdown = ({ 
  user, 
  isDark, 
  onLogout, 
  onManageProfiles 
}) => {
  const navigate = useNavigate();

  const handleNavigation = (path, label) => {
    navigate(path);
    announceToScreenReader(`Navigated to ${label}`);
  };

  const handleLogout = () => {
    onLogout();
    announceToScreenReader('Signed out successfully');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={`hidden md:flex items-center gap-2 p-1.5 rounded-xl active:scale-95 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2 ${
            isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}
          data-testid="profile-dropdown"
          aria-label={`Profile menu for ${user?.username || 'User'}. Press Enter to open menu.`}
          aria-haspopup="menu"
        >
          <Avatar className={`w-8 h-8 ring-2 ${isDark ? 'ring-white/20' : 'ring-gray-300'}`}>
            <AvatarImage 
              src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.username || 'user'}`} 
              alt={`${user?.username || 'User'}'s profile picture`}
            />
            <AvatarFallback className={isDark ? 'bg-gray-700' : 'bg-gray-200'}>
              <User className={`w-4 h-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} aria-hidden="true" />
              <span className="sr-only">{user?.username || 'User'}</span>
            </AvatarFallback>
          </Avatar>
          <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className={`w-56 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
        role="menu"
        aria-label="Profile menu"
      >
        {user && (
          <DropdownMenuLabel className={`px-3 py-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.username}</p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
          </DropdownMenuLabel>
        )}
        <DropdownMenuSeparator className={isDark ? 'bg-gray-800' : 'bg-gray-200'} />
        
        {user?.role === 'admin' && (
          <DropdownMenuItem 
            className="text-yellow-400 hover:bg-yellow-500/10 cursor-pointer gap-2 py-2.5 focus:bg-yellow-500/10 focus:text-yellow-400"
            onClick={() => handleNavigation('/admin', 'Admin Portal')}
            data-testid="admin-portal-link"
            role="menuitem"
          >
            <Shield className="w-4 h-4" aria-hidden="true" />
            <span>Admin Portal</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem 
          className={`cursor-pointer gap-2 py-2.5 ${isDark ? 'text-gray-300 hover:bg-white/5 focus:bg-white/5' : 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100'}`}
          onClick={onManageProfiles}
          role="menuitem"
        >
          <Users className="w-4 h-4" aria-hidden="true" />
          <span>Manage Profiles</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          className={`cursor-pointer gap-2 py-2.5 ${isDark ? 'text-gray-300 hover:bg-white/5 focus:bg-white/5' : 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100'}`}
          onClick={() => handleNavigation('/account-settings', 'Account Settings')}
          role="menuitem"
        >
          <Settings className="w-4 h-4" aria-hidden="true" />
          <span>Account Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          className={`cursor-pointer gap-2 py-2.5 ${isDark ? 'text-gray-300 hover:bg-white/5 focus:bg-white/5' : 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100'}`}
          onClick={() => handleNavigation('/help', 'Help Center')}
          role="menuitem"
        >
          <HelpCircle className="w-4 h-4" aria-hidden="true" />
          <span>Help Center</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className={isDark ? 'bg-gray-800' : 'bg-gray-200'} />
        <DropdownMenuItem 
          className="text-red-400 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-400 cursor-pointer gap-2 py-2.5"
          onClick={handleLogout}
          data-testid="logout-btn"
          role="menuitem"
        >
          <LogOut className="w-4 h-4" aria-hidden="true" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileDropdown;
